import React from 'react';

import PropTypes from 'prop-types';

// Components
import { Mui } from 'components';
import Label from 'components/layouts/Label';

// Local components
import Base from './Base';

const STATUS_TYPES = {
  order_created: 'order-created',
  payment_pending: 'payment-pending',
  payment_approved: 'payment-approved',
  payment_denied: 'payment-denied',
  handling: 'handling',
  invoice: 'invoice',
  invoiced: 'invoiced',
  delivered: 'delivered',
  cancellation_requested: 'cancellation-requested',
  cancel: 'cancel',
  canceled: 'canceled',
  reversed: 'reversed',
  pending: 'pending',
  active: 'active',
  finished: 'finished',
  transferred: 'transferred',
};

export const ORDER_STATUS = {
  'order-created': {
    status: STATUS_TYPES.order_created,
    color: 'primary',
    icon: 'create_new_folder_outlined',
    label: 'Pedido criado',
    recommended: false,
  },
  'payment-pending': {
    status: STATUS_TYPES.payment_pending,
    color: 'warning',
    icon: 'access_time',
    label: 'Pagamento pendente',
    recommended: false,
  },
  'payment-approved': {
    status: STATUS_TYPES.payment_approved,
    color: 'success',
    icon: 'monetization_on',
    label: 'Pagamento aprovado',
    recommended: true,
  },
  'payment-denied': {
    status: STATUS_TYPES.payment_denied,
    color: 'error',
    icon: 'error',
    label: 'Pedido negado',
    recommended: true,
  },
  handling: {
    status: STATUS_TYPES.handling,
    color: 'secondary',
    icon: 'pan_tool',
    label: 'Em manuseio',
    recommended: true,
  },
  invoice: {
    status: STATUS_TYPES.invoice,
    color: 'primary',
    icon: 'file_copy',
    label: 'Faturando',
    recommended: false,
  },
  invoiced: {
    status: STATUS_TYPES.invoiced,
    color: 'success',
    icon: 'receipt',
    label: 'Faturado',
    recommended: false,
  },
  delivered: {
    status: STATUS_TYPES.delivered,
    color: 'success',
    icon: 'local_shipping',
    label: 'Entregue',
    recommended: false,
  },
  'cancellation-requested': {
    status: STATUS_TYPES.cancellation_requested,
    color: 'info',
    icon: 'live_help',
    label: 'Cancelamento solicitado',
    recommended: true,
  },
  cancel: {
    status: STATUS_TYPES.cancel,
    color: 'info',
    icon: 'cancel',
    label: 'Cancelando',
    recommended: false,
  },
  canceled: {
    status: STATUS_TYPES.canceled,
    color: 'error',
    icon: 'archive',
    label: 'Cancelado',
    recommended: false,
  },
  reversed: {
    status: STATUS_TYPES.reversed,
    color: 'error',
    icon: 'restore',
    label: 'Revertido',
    recommended: false,
  },
};

export const CONTRACT_STATUS = {
  'order-created': {
    status: STATUS_TYPES.order_created,
    color: 'primary',
    icon: 'create_new_folder_outlined',
    label: 'Pedido criado',
    recommended: false,
  },
  'payment-pending': {
    status: STATUS_TYPES.payment_pending,
    color: 'warning',
    icon: 'access_time',
    label: 'Pagamento pendente',
  },
  'payment-approved': {
    status: STATUS_TYPES.payment_approved,
    color: 'success',
    icon: 'monetization_on',
    label: 'Pagamento aprovado',
  },
  pending: {
    status: STATUS_TYPES.pending,
    color: 'warning',
    icon: 'access_time',
    label: 'Pendente',
  },
  active: {
    status: STATUS_TYPES.active,
    color: 'success',
    icon: 'play_circle_filled',
    label: 'Ativo',
  },
  invoice: {
    status: STATUS_TYPES.invoice,
    color: 'primary',
    icon: 'file_copy',
    label: 'Faturando',
    recommended: false,
  },
  'cancellation-requested': {
    status: STATUS_TYPES.cancellation_requested,
    color: 'info',
    icon: 'live_help',
    label: 'Cancelamento de contrato solicitado',
  },
  canceled: {
    status: STATUS_TYPES.canceled,
    color: 'error',
    icon: 'archive',
    label: 'Cancelado',
  },
  finished: {
    status: STATUS_TYPES.finished,
    color: 'success',
    icon: 'check_circle_outlined',
    label: 'Finalizado',
  },
};

export const TUITION_STATUS = {
  pending: {
    status: STATUS_TYPES.pending,
    color: 'warning',
    icon: 'access_time',
    label: 'Pendente',
  },
  active: {
    status: STATUS_TYPES.active,
    color: 'success',
    icon: 'play_circle_filled',
    label: 'Ativo',
  },
  'cancellation-requested': {
    status: STATUS_TYPES.cancellation_requested,
    color: 'info',
    icon: 'live_help',
    label: 'Cancelamento de mensalidade solicitado',
  },
  canceled: {
    status: STATUS_TYPES.canceled,
    color: 'error',
    icon: 'archive',
    label: 'Cancelado',
  },
  finished: {
    status: STATUS_TYPES.finished,
    color: 'success',
    icon: 'check_circle_outlined',
    label: 'Finalizado',
  },
  transferred: {
    status: STATUS_TYPES.transferred,
    color: 'secondary',
    icon: 'send',
    label: 'Transferido',
  },
};

export const RECURRENCE_STATUS = {
  pending: {
    status: STATUS_TYPES.pending,
    color: 'warning',
    icon: 'access_time',
    label: 'Pendente',
  },
  active: {
    status: STATUS_TYPES.active,
    color: 'success',
    icon: 'play_circle_filled',
    label: 'Ativo',
  },
  finished: {
    status: STATUS_TYPES.finished,
    color: 'success',
    icon: 'check_circle_outlined',
    label: 'Finalizado',
  },
  'cancellation-requested': {
    status: STATUS_TYPES.cancellation_requested,
    color: 'info',
    icon: 'live_help',
    label: 'Cancelamento de recorrência solicitado',
  },
  canceled: {
    status: STATUS_TYPES.canceled,
    color: 'error',
    icon: 'archive',
    label: 'Cancelado',
  },
  transferred: {
    status: STATUS_TYPES.transferred,
    color: 'secondary',
    icon: 'send',
    label: 'Transferido',
  },
};

const setDefaultStatus = objStatus =>
  Object.keys(objStatus).map(key => ({ ...objStatus[key], key }));

const getDefaultValues = (values, objDefaultStatus) => {
  const defaultValues = [];

  values.map(item => {
    const [option] = objDefaultStatus.filter(status => status.key === item);

    if (option) defaultValues.push(option);

    return item;
  });

  return defaultValues;
};

export const helpers = { setDefaultStatus, getDefaultValues };

export const OrderTags = ({ isReverted, hasExternalPayment, hasExternalRefund }) => {
  const TAG_DEFINITIONS = [
    {
      prop: 'isReverted',
      color: 'error',
      icon: 'settings_backup_restore',
      message: 'Pedido estornado',
    },
    {
      prop: 'hasExternalPayment',
      color: 'info',
      icon: 'account_balance_wallet',
      message: 'Pagamento externo',
    },
    {
      prop: 'hasExternalRefund',
      color: 'warning',
      icon: 'money_off',
      message: 'Reembolso externo',
    },
  ];

  const props = { isReverted, hasExternalPayment, hasExternalRefund };
  const tags = TAG_DEFINITIONS.filter(tagDef => props[tagDef.prop]);

  if (tags.length === 0) return null;

  return (
    <Mui.Box
      mt={0.5}
      display="flex"
      flexWrap="nowrap"
      style={{
        overflowX: 'hidden',
        overflowY: 'hidden',
        whiteSpace: 'nowrap',
      }}>
      {tags.map(tag => (
        <Mui.Box key={tag.icon} mr={0.5} style={{ flexShrink: 0 }}>
          <Label color={tag.color} size="xs">
            <Mui.Box
              width="100%"
              px={0.25}
              py={0.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ whiteSpace: 'nowrap' }}>
              <Mui.Icon style={{ fontSize: '14px' }}>{tag.icon}</Mui.Icon>
              <Mui.Box px={0.25} />
              {tag.message}
            </Mui.Box>
          </Label>
        </Mui.Box>
      ))}
    </Mui.Box>
  );
};

OrderTags.propTypes = {
  isReverted: PropTypes.bool,
  hasExternalPayment: PropTypes.bool,
  hasExternalRefund: PropTypes.bool,
};

function Single(props) {
  const {
    status,
    statusDisplay,
    isReverted,
    hasExternalPayment,
    hasExternalRefund,
    ...otherProps
  } = props;

  if (!status) return null;

  return (
    <Base
      color={ORDER_STATUS[status]?.color || 'inherit'}
      icon={ORDER_STATUS[status]?.icon}
      {...otherProps}>
      {statusDisplay || ORDER_STATUS[status]?.label}
      <OrderTags
        isReverted={isReverted}
        hasExternalPayment={hasExternalPayment}
        hasExternalRefund={hasExternalRefund}
      />
    </Base>
  );
}

Single.propTypes = {
  status: PropTypes.string,
  statusDisplay: PropTypes.string,
  isReverted: PropTypes.bool,
  hasExternalPayment: PropTypes.bool,
  hasExternalRefund: PropTypes.bool,
};

function Contract(props) {
  const {
    status,
    statusDisplay,
    isReverted,
    hasExternalPayment,
    hasExternalRefund,
    ...otherProps
  } = props;

  if (!status) return null;

  return (
    <Base
      color={CONTRACT_STATUS[status]?.color || 'inherit'}
      icon={CONTRACT_STATUS[status]?.icon}
      {...otherProps}>
      {statusDisplay || CONTRACT_STATUS[status]?.label}
      <OrderTags
        isReverted={isReverted}
        hasExternalPayment={hasExternalPayment}
        hasExternalRefund={hasExternalRefund}
      />
    </Base>
  );
}

Contract.propTypes = {
  status: PropTypes.string,
  statusDisplay: PropTypes.string,
  isReverted: PropTypes.bool,
  hasExternalPayment: PropTypes.bool,
  hasExternalRefund: PropTypes.bool,
};

function Tuition(props) {
  const {
    status,
    statusDisplay,
    isReverted,
    hasExternalPayment,
    hasExternalRefund,
    ...otherProps
  } = props;

  if (!status) return null;

  return (
    <Base
      color={TUITION_STATUS[status]?.color || 'inherit'}
      icon={TUITION_STATUS[status]?.icon}
      {...otherProps}>
      {statusDisplay || TUITION_STATUS[status]?.label}
      <OrderTags
        isReverted={isReverted}
        hasExternalPayment={hasExternalPayment}
        hasExternalRefund={hasExternalRefund}
      />
    </Base>
  );
}

Tuition.propTypes = {
  status: PropTypes.string,
  statusDisplay: PropTypes.string,
  isReverted: PropTypes.bool,
  hasExternalPayment: PropTypes.bool,
  hasExternalRefund: PropTypes.bool,
};

function Order(props) {
  const {
    type,
    progressBar,
    variant,
    orderNumber,
    isReverted,
    hasExternalPayment,
    hasExternalRefund,
    ...otherProps
  } = props;

  const getCaption = () => {
    if (!orderNumber) return undefined;

    if (type === 'contract') return `Contrato ${orderNumber}`;
    if (type === 'tuition') return `Mensalidade ${orderNumber}`;
    return `Pedido ${orderNumber}`;
  };

  const commonProps = {
    progressBar,
    variant,
    caption: getCaption(),
    captionFieldProps: { color: 'textPrimary', variant: 'body1', noWrap: true },
    isReverted,
    hasExternalPayment,
    hasExternalRefund,
    ...otherProps,
  };

  if (type === 'contract') return <Contract {...commonProps} />;
  if (type === 'tuition') return <Tuition {...commonProps} />;
  return <Single {...commonProps} />;
}

Order.propTypes = {
  type: PropTypes.oneOf(['order', 'contract', 'tuition']),
  progressBar: PropTypes.bool,
  variant: PropTypes.oneOf(['standard', 'filled']),
  orderNumber: PropTypes.string,
  isReverted: PropTypes.bool,
  hasExternalPayment: PropTypes.bool,
  hasExternalRefund: PropTypes.bool,
};

Order.defaultProps = {
  progressBar: true,
  variant: 'filled',
};

export default Order;
